import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { Observable, Subscription, forkJoin, of } from 'rxjs';

import { NotificationService } from 'src/app/shared/services/notification.service';
import { BaseServices } from '../../../../kuba.services';
import { SurveyParticipantDto } from '../../surveys/models';
import { WorkEnvironmentSurveyService } from '../../surveys/services/work-environment-survey.service';

/** The three steps of the wizard, in order. */
const STEPS = ['DETAILS', 'PARTICIPANTS', 'SUMMARY'];

/**
 * Create or edit a survey, in three steps: details, participants, summary.
 *
 * Ported from the AMIL `ManageSurveyDialog` and the `SurveyDetails`,
 * `SurveyParticipants` and `SurveySummary` steps it rendered. The MUI stepper
 * becomes a `p-dialog` with its own step header, and the participant grid a
 * `p-table` with checkbox selection.
 *
 * Two differences from the prototype, both deliberate:
 *  - It validates. The prototype would happily save a survey with no title and
 *    an end date before its start date.
 *  - Saving happens on the summary step's Save button. The prototype advanced
 *    to a fourth step and fired the request from a `useEffect` watching the
 *    step index, which submitted again on every re-render of that step.
 */
@Component({
  selector: 'survey-wizard',
  templateUrl: './survey-wizard.component.html',
  styleUrls: ['./survey-wizard.component.scss'],
  standalone: false
})
export class SurveyWizardComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Null creates a new survey; an id edits that survey. */
  @Input() surveyId: number | null = null;

  /** Raised after a successful save. */
  @Output() saved = new EventEmitter<void>();

  //#region Variables
  private readonly notifications = inject(NotificationService);
  private readonly businessId = BaseServices.BusinessId;

  readonly steps = STEPS;
  stepIndex = 0;

  title = '';
  startDate: Date | null = null;
  endDate: Date | null = null;
  reminderDate: Date | null = null;

  participants: SurveyParticipantDto[] = [];
  selectedParticipants: SurveyParticipantDto[] = [];

  isLoading = false;
  isSaving = false;
  detailsTouched = false;

  private subscriptions: Subscription[] = [];
  //#endregion

  constructor(private surveyService: WorkEnvironmentSurveyService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // `in` rather than a truthiness check: without strictNullChecks the index
    // signature is typed as always present, so `changes['visible']` reads as a
    // condition that is always true even though it is undefined at runtime
    // whenever another input changed on its own.
    if ('visible' in changes && this.visible) {
      this.reset();
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  //#region State
  get isEdit(): boolean {
    return this.surveyId !== null;
  }

  get selectedCount(): number {
    return this.selectedParticipants.length;
  }

  get isDetailsValid(): boolean {
    return (
      this.title.trim().length > 0 &&
      this.startDate !== null &&
      this.endDate !== null &&
      this.reminderDate !== null &&
      this.endDate >= this.startDate
    );
  }

  get isEndBeforeStart(): boolean {
    return (
      this.startDate !== null &&
      this.endDate !== null &&
      this.endDate < this.startDate
    );
  }

  private reset(): void {
    this.stepIndex = 0;
    this.title = '';
    this.startDate = null;
    this.endDate = null;
    this.reminderDate = null;
    this.selectedParticipants = [];
    this.detailsTouched = false;
    this.isSaving = false;
  }
  //#endregion

  //#region Data
  private load(): void {
    this.isLoading = true;

    this.subscriptions.push(
      forkJoin({
        participants: this.surveyService.getParticipants(this.businessId),
        survey: this.surveyId
          ? this.surveyService.getById(this.surveyId)
          : of(null)
      }).subscribe({
        next: result => {
          this.participants = result.participants;

          const survey = result.survey;

          if (survey !== null) {
            this.title = survey.Title;
            this.startDate = this.toDate(survey.StartDate);
            this.endDate = this.toDate(survey.EndDate);
            this.reminderDate = this.toDate(survey.ReminderDate);
            this.selectedParticipants = this.participants.filter(participant =>
              survey.ParticipantIds.includes(participant.Id)
            );
          }

          this.isLoading = false;
        },
        error: () => {
          this.participants = [];
          this.isLoading = false;
        }
      })
    );
  }
  //#endregion

  //#region Navigation
  next(): void {
    if (this.stepIndex === 0) {
      this.detailsTouched = true;

      if (!this.isDetailsValid) {
        if (this.isEndBeforeStart) {
          this.notifications.warn('UI_NOTIFICATIONS.END_AFTER_START');
        }
        return;
      }
    }

    if (this.stepIndex < this.steps.length - 1) {
      this.stepIndex++;
    }
  }

  back(): void {
    if (this.stepIndex > 0) {
      this.stepIndex--;
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
  //#endregion

  //#region Save
  save(): void {
    if (!this.isDetailsValid || this.isSaving) {
      return;
    }

    const payload = {
      Title: this.title.trim(),
      StartDate: this.toIsoDate(this.startDate as Date),
      EndDate: this.toIsoDate(this.endDate as Date),
      ReminderDate: this.toIsoDate(this.reminderDate as Date),
      ParticipantIds: this.selectedParticipants.map(
        participant => participant.Id
      )
    };

    this.isSaving = true;

    // Create returns the new id and update returns nothing; neither is used
    // here, so the two are widened to one type the caller can subscribe to.
    const request: Observable<unknown> = this.surveyId
      ? this.surveyService.update(this.surveyId, payload)
      : this.surveyService.create(this.businessId, payload);

    this.subscriptions.push(
      request.subscribe({
        next: () => {
          this.isSaving = false;
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.saved.emit();
          this.close();
        },
        error: () => {
          this.isSaving = false;
        }
      })
    );
  }
  //#endregion

  //#region Dates
  /**
   * Parses an ISO date (yyyy-MM-dd) as a local date. `new Date(iso)` would
   * read it as UTC midnight, which lands on the previous day west of Greenwich.
   */
  private toDate(iso: string): Date | null {
    if (!iso) {
      return null;
    }

    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /** Formats a local date as ISO (yyyy-MM-dd), without shifting the day. */
  private toIsoDate(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
  //#endregion
}
